import { useEffect, useState, useCallback } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { useShop } from "@/contexts/ShopContext";
import Modal from "@/components/Modal";
import { toast } from "sonner";
import { categoryFallbackImage } from "@/constants/categoryImages";

const CATEGORIES = ["Food", "Drinks", "Take Away", "Misc"];

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

export default function Inventory() {
  const { currentShopId } = useShop();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Food", price: "", stock: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!currentShopId) return;
    const { data } = await api.get("/menu-items", { params: { shop_id: currentShopId } });
    setItems(data);
  }, [currentShopId]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((i) =>
    (!catFilter || i.category === catFilter) &&
    (!search || i.name.toLowerCase().includes(search.toLowerCase()))
  );

  const openAdd = () => {
    setEditing(null); setForm({ name: "", category: "Food", price: "", stock: "" });
    setImageFile(null); setImagePreview(null); setModalOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item); setForm({ name: item.name, category: item.category, price: item.price, stock: item.stock ?? "" });
    setImageFile(null); setImagePreview(resolveImageUrl(item.image_url)); setModalOpen(true);
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be smaller than 5MB"); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = async (itemId) => {
    const fd = new FormData();
    fd.append("file", imageFile);
    await api.post(`/menu-items/${itemId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const save = async () => {
    if (saving) return;
    if (!form.name.trim() || !form.price) { toast.error("Name and price are required"); return; }
    setSaving(true);
    try {
      const stockValue = form.stock === "" ? null : parseInt(form.stock, 10);
      const payload = { name: form.name, category: form.category, price: parseFloat(form.price), stock: stockValue };
      let itemId = editing?.id;
      if (editing) {
        await api.patch(`/menu-items/${editing.id}`, payload);
      } else {
        const { data } = await api.post("/menu-items", { ...payload, shop_id: currentShopId });
        itemId = data.id;
      }
      if (imageFile && itemId) {
        await uploadImage(itemId);
      }
      toast.success("Saved");
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save item");
    } finally {
      setSaving(false);
    }
  };

  const saveStock = async (item, value) => {
    await api.patch(`/menu-items/${item.id}/stock`, { stock: value === "" ? null : parseInt(value, 10) });
    toast.success("Stock updated");
    load();
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/menu-items/${item.id}`);
      toast.success("Item deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete item");
    }
  };

  return (
    <section className="view" data-testid="inventory-page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Menu, stock &amp; images</div>
          <h1>Inventory</h1>
          <p>Add items so staff can start taking orders.</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd} data-testid="add-item-btn">＋ Add item</button>
      </div>
      <div className="card">
        <div className="toolbar">
          <input className="input" placeholder="⌕  Search menu items" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inventory-search-input" />
          <select className="select" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="inventory-category-filter">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
        <div className="inv-grid">
          {filtered.map((item) => (
            <div className="inv-card" key={item.id} data-testid={`inventory-card-${item.id}`}>
              <div className="inv-thumb">{item.image_url ? <img src={resolveImageUrl(item.image_url)} alt={item.name} /> : <img src={categoryFallbackImage(item.category)} alt={item.category} />}</div>
              <div className="inv-body">
                <strong>{item.name}</strong>
                <div className="inv-meta">{item.category} · ₹{item.price}</div>
                <div className="stock-row">
                  <input
                    type="number" min="0" placeholder="Unlimited" defaultValue={item.stock ?? ""}
                    id={`stock-${item.id}`} data-testid={`stock-input-${item.id}`}
                  />
                  <button className="btn btn-secondary" onClick={() => saveStock(item, document.getElementById(`stock-${item.id}`).value)} data-testid={`save-stock-${item.id}`}>Save</button>
                </div>
                <div className="inv-actions">
                  <button className="btn btn-secondary" onClick={() => openEdit(item)} data-testid={`edit-item-${item.id}`}>Edit</button>
                  <button className="btn btn-danger" onClick={() => deleteItem(item)} data-testid={`remove-item-${item.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="empty" style={{ gridColumn: "1/-1" }}>No menu items yet</div>}
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit item" : "Add item"}
        footer={<button className="btn btn-primary" disabled={saving} onClick={save} data-testid="save-item-btn">{saving ? "Saving…" : "Save item"}</button>}>
        <div>
          <label className="field-label">Photo</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 64, height: 64, borderRadius: 10, overflow: "hidden", background: "var(--surface-2)", border: "1px solid var(--border)", flex: "none" }}>
              {imagePreview ? <img src={imagePreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
            </div>
            <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer" }}>
              Choose image
              <input type="file" accept="image/*" onChange={onPickImage} style={{ display: "none" }} data-testid="item-image-input" />
            </label>
          </div>
        </div>
        <div><label className="field-label">Name</label><input className="input" style={{ width: "100%" }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name-input" /></div>
        <div><label className="field-label">Category</label>
          <select className="select" style={{ width: "100%" }} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="item-category-select">
            {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
        <div><label className="field-label">Price (₹)</label><input type="number" className="input" style={{ width: "100%" }} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="item-price-input" /></div>
        <div><label className="field-label">Stock (blank = unlimited)</label><input type="number" min="0" className="input" style={{ width: "100%" }} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="item-stock-input" /></div>
      </Modal>
    </section>
  );
}